import { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import { useWindowDimensions } from "../hooks";

/**
 * This hook will estimate the amount of items that can be shown on a single page in the slider.
 * Useful to decided if a static version of the slider can be shown instead of a paginated one.
 *
 * @param itemsLength amount of items that will be rendered
 * @param itemWidth the width of each item
 * @param maxContainerWidth the max width of the page container
 * @param horizontalContainerOffset padding-left (or right) that the container has in relation to the viewport
 * @param desktopSliderWidthFraction percentage of the parent that the slider can take in
 *
 * @returns a boolean describing if the static slider can be used instead
 */
const useItemSliderSinglePage = (
    itemsLength: number,
    itemWidth: number,
    maxContainerWidth: number,
    horizontalContainerOffset: number,
    desktopSliderWidthFraction: number,
): boolean => {
    const { width } = useWindowDimensions();
    const { breakpoints } = useTheme();
    const [maxStaticCount, setMaxStaticCount] = useState(0);

    // default to always show static slider during SSR
    const fitsOnSinglePage = maxStaticCount ? itemsLength <= maxStaticCount : true;

    useEffect(() => {
        // divide the window width (on desktop container size is 1200) by the width of a single swatch
        const containerWidth = width < parseInt(breakpoints.lg) ? width : Math.min(width, maxContainerWidth);
        // the option's description is displayed next to the slider on desktop layouts
        const limitedContainerWidth =
            (width < parseInt(breakpoints.lg) ? containerWidth : desktopSliderWidthFraction * containerWidth) -
            horizontalContainerOffset * 2;
        // round down and subtract one to be sure
        const count = limitedContainerWidth / itemWidth;
        setMaxStaticCount(count);
    }, [width, breakpoints, itemWidth, desktopSliderWidthFraction, maxContainerWidth, horizontalContainerOffset]);

    return fitsOnSinglePage;
};

export default useItemSliderSinglePage;
